import { useState } from 'react';
import parse from 'html-react-parser';
import {
  ParkingBookFlowFormItemPropertiesModel,
  ParkingBookFlowWherePropertiesModel,
  ParkingBookFlowHowManyPropertiesModel,
  ParkingBookFlowWhenPropertiesModel,
  ParkingBookFlowWhereElementModelContentType,
  ParkingBookFlowHowManyElementModelContentType,
  ParkingBookFlowWhenElementModelContentType,
} from '@/services/platform/api/model';
import { Icon } from '@/components/ui/icon';
import { Button } from '@/components/ui/button';
import { ProgressDots } from '@/components/shared/progressDots';
import { FormRow } from '@/components/shared/formRow';
import { WhereSheet } from './sheets/where';
import { HowManySheet } from './sheets/howMany';
import { WhenSheet } from './sheets/when';
import { formatDate, formatTime } from '@/lib/core/utils';
import type { BookingState, OpenSheet, ParkingBookProps } from './types';
import { useClientTranslation } from '@/lib/core/i18n';

// ── ParkingBook ───────────────────────────────────────────────────────────────

export function ParkingBook({
  cmsProps,
  booking,
  onBookingChange,
  onBack,
  onContinue,
  onRequestSuccess,
  context,
  step,
}: ParkingBookProps) {
  const t = useClientTranslation();
  const [openSheet, setOpenSheet] = useState<OpenSheet>(null);

  const formItems = (cmsProps?.bookForm?.items ?? []).map(
    item => item.content?.properties as ParkingBookFlowFormItemPropertiesModel | undefined
  );

  const titleHtml = cmsProps?.title?.markup;
  const continueLabel = cmsProps?.continueLabel ?? t('App.Labels.Continue');

  const canContinue =
    booking.venue !== null && booking.guestCount !== null && booking.date !== null && booking.time !== null;

  const venueDisplay = booking.venue?.name ?? '';
  const guestDisplay = booking.guestCount !== null ? `${booking.guestCount}` : '';
  const whenDisplay =
    booking.date && booking.time
      ? `${formatDate(booking.date.toISOString())}, ${formatTime(booking.time)}`
      : '';

  const rowIsSet = formItems.map(formItem => {
    const stepType = formItem?.stepPicker?.items?.[0]?.content?.contentType;
    switch (stepType) {
      case ParkingBookFlowWhereElementModelContentType.parkingBookFlowWhere:
        return !!booking.venue;
      case ParkingBookFlowHowManyElementModelContentType.parkingBookFlowHowMany:
        return booking.guestCount !== null;
      case ParkingBookFlowWhenElementModelContentType.parkingBookFlowWhen:
        return !!(booking.date && booking.time);
      default:
        return false;
    }
  });

  const isRowDisabled = (index: number) => index > 0 && !rowIsSet.slice(0, index).every(Boolean);

  const handleWhereChange = (updates: Partial<BookingState>) =>
    onBookingChange(
      'venue' in updates
        ? {
            ...updates,
            guestCount: null,
            date: null,
            time: null,
            room: null,
            seatingAreaPublicGuid: undefined,
          }
        : updates
    );

  const handleHowManyChange = (updates: Partial<BookingState>) =>
    onBookingChange(
      'guestCount' in updates
        ? {
            ...updates,
            date: null,
            time: null,
            room: null,
            seatingAreaPublicGuid: undefined,
          }
        : updates
    );

  return (
    <>
      {/* Header */}
      <header className="flex shrink-0 items-center justify-between px-4 py-2">
        <button
          type="button"
          aria-label="Go back"
          className="flex size-11 cursor-pointer items-center justify-center"
          onClick={onBack}
        >
          <Icon
            name="long-arrow-up-left"
            width={24}
            height={24}
            strokeWidth={2}
            className="text-jncquoi-neutral-1000"
          />
        </button>
        <ProgressDots step={step} />
      </header>

      {/* Scrollable content */}
      <div className="flex flex-1 flex-col gap-8 overflow-y-auto px-4 pt-6 pb-24">
        {titleHtml && <div className="book-intro-title">{parse(titleHtml)}</div>}

        {/* Form rows */}
        <div className="flex flex-col gap-6">
          {formItems.map((formItem, index) => {
            const step = formItem?.stepPicker?.items?.[0];
            const stepTitle = formItem?.title;
            const stepType = step?.content?.contentType;
            switch (stepType) {
              case ParkingBookFlowWhereElementModelContentType.parkingBookFlowWhere:
                return (
                  <FormRow
                    key={index}
                    label={stepTitle ?? t('App.Labels.Where')}
                    value={venueDisplay}
                    isEmpty={!booking.venue}
                    disabled={isRowDisabled(index)}
                    onClick={() => setOpenSheet('where')}
                  />
                );
              case ParkingBookFlowHowManyElementModelContentType.parkingBookFlowHowMany:
                return (
                  <FormRow
                    key={index}
                    label={stepTitle ?? t('App.Labels.HowMany')}
                    value={guestDisplay}
                    isEmpty={booking.guestCount === null}
                    disabled={isRowDisabled(index)}
                    onClick={() => setOpenSheet('howmany')}
                  />
                );
              case ParkingBookFlowWhenElementModelContentType.parkingBookFlowWhen:
                return (
                  <FormRow
                    key={index}
                    label={stepTitle ?? t('App.Labels.When')}
                    value={whenDisplay}
                    isEmpty={!booking.date || !booking.time}
                    disabled={isRowDisabled(index)}
                    onClick={() => setOpenSheet('when')}
                  />
                );
              default:
                return null;
            }
          })}
        </div>
      </div>

      {/* Sticky continue button */}
      <div className="fixed bottom-0 w-full shrink-0 border-t-[0.5px] border-[#c8c8c8] bg-white px-4 pt-6 pb-10.5">
        <Button
          variant="primary-solid"
          palette="dark"
          size="lg"
          className="w-full"
          disabled={!canContinue}
          onClick={onContinue}
        >
          {continueLabel}
        </Button>
      </div>

      {/* Sheets */}

      {formItems.map((formItem, index) => {
        const step = formItem?.stepPicker?.items?.[0];
        const stepProps = step?.content?.properties;
        const stepType = step?.content?.contentType;
        switch (stepType) {
          case ParkingBookFlowWhereElementModelContentType.parkingBookFlowWhere:
            return (
              <WhereSheet
                key={index}
                isOpen={openSheet === 'where'}
                onClose={() => setOpenSheet(null)}
                cmsProps={stepProps as ParkingBookFlowWherePropertiesModel}
                booking={booking}
                onBookingChange={handleWhereChange}
                context={context}
              />
            );
          case ParkingBookFlowHowManyElementModelContentType.parkingBookFlowHowMany:
            return (
              <HowManySheet
                key={index}
                isOpen={openSheet === 'howmany'}
                onClose={() => setOpenSheet(null)}
                cmsProps={stepProps as ParkingBookFlowHowManyPropertiesModel}
                booking={booking}
                onBookingChange={handleHowManyChange}
              />
            );
          case ParkingBookFlowWhenElementModelContentType.parkingBookFlowWhen:
            return (
              <WhenSheet
                key={index}
                isOpen={openSheet === 'when'}
                onClose={() => setOpenSheet(null)}
                cmsProps={stepProps as ParkingBookFlowWhenPropertiesModel}
                booking={booking}
                onBookingChange={onBookingChange}
                onRequestSuccess={onRequestSuccess}
              />
            );
          default:
            return null;
        }
      })}
    </>
  );
}
